<script setup lang="ts">
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { MaskaDetail, MaskInputOptions } from 'maska';
import { computed, reactive } from 'vue';
import { dateHelpers } from '@hfc/ui-core';

const props = withDefaults(defineProps<{
  id: string,
  modelValue?: number,
  size?: string,
  label: string,
  hasErrors?: boolean,
  isValid?: boolean,
  showRequiredOnLabel?: boolean,
  inputClass?: string,
  autoFocus?: boolean
}>(), {
  size: 'h-16'
});

const { fromUtcTimestampToLocaleTimestamp, fromLocaleDateToUtcTimestamp } = dateHelpers();

const localDate = computed(() => props.modelValue ? fromUtcTimestampToLocaleTimestamp(Number(props.modelValue)) : undefined);

interface EmitsInterface {
  (event: 'update:modelValue', value: number): void,
  (event: 'change', value: number): void,
}

const emits = defineEmits<EmitsInterface>();

const emitChange = (value: Date): void => {
  emits('update:modelValue', fromLocaleDateToUtcTimestamp(value));
  emits('change', fromLocaleDateToUtcTimestamp(value));
};

const maskObject = reactive({} as MaskaDetail);
const maskOptions: MaskInputOptions = {
  onMaska: (detail: MaskaDetail): void => {
    Object.assign(maskObject, detail);
  }
};

const textInputOptions = {
  format: 'MM/dd/yyyy',
  openMenu: false
};
</script>

<template>
  <div class="flex flex-col">
    <label v-if="label" :for="id" class="font-medium mb-1 text-sky-700 block">
      {{ label }}<span v-if="showRequiredOnLabel" class="font-serif">*</span>
    </label>
    <Datepicker
      v-bind:model-value="localDate"
      :time-config="{ enableTimePicker: false }"
      auto-apply
      :text-input="textInputOptions"
      :formats="{ input: 'MM/dd/yyyy' }"
      :input-attrs="{ clearable: false }"
      @update:model-value="emitChange"
    >
      <template #dp-input="{ value, onInput, onBlur, toggleMenu, onEnter }">
        <div class="flex items-center">
          <input
            class="text-lg"
            :class="[{ 'error': hasErrors, 'valid touched value': isValid }, size, inputClass]"
            placeholder="MM/DD/YYYY"
            type="text"
            :value="value"
            data-maska="##/##/####"
            data-maska-eager
            v-maska="maskOptions"
            v-autofocus="autoFocus && !modelValue"
            @input="onInput"
            @blur="onBlur"
            @keydown.enter="onEnter"
          />
          <hfc-icon :icon="['fa', 'calendar-day']" tabindex="-1" @click="toggleMenu" class="absolute right-4 z-50 bg-white hover:cursor-pointer"/>
        </div>
      </template>
    </Datepicker>
  </div>
</template>
